import { textareaCaretLineTopFromContentTop } from "./textareaCaretGeometry.js";

function parseCssPx(raw: string): number {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function expandTabsToSpacesOneLine(line: string, tabSize: number): string {
  const ts = Math.max(1, Math.floor(tabSize));
  let out = "";
  let col = 0;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (ch === "\t") {
      const sp = ts - (col % ts);
      out += " ".repeat(sp);
      col += sp;
    } else {
      out += ch;
      col++;
    }
  }
  return out;
}

/**
 * Map a client-space pointer to a UTF-16 offset in `ta.value`.
 * Uses `document.caretPositionFromPoint` when it targets the textarea; otherwise a mirror + binary search.
 */
export function textareaOffsetAtClientPoint(ta: HTMLTextAreaElement, clientX: number, clientY: number): number {
  const len = ta.value.length;
  const rect = ta.getBoundingClientRect();
  const cs = getComputedStyle(ta);
  const padL = parseCssPx(cs.paddingLeft);
  const padT = parseCssPx(cs.paddingTop);
  const yContent = clientY - rect.top - padT + ta.scrollTop;
  const xContent = clientX - rect.left - padL + ta.scrollLeft;

  const doc = ta.ownerDocument;
  const cfp = (doc as Document & { caretPositionFromPoint?: (x: number, y: number) => CaretPosition | null })
    .caretPositionFromPoint;
  if (typeof cfp === "function") {
    try {
      const pos = cfp.call(doc, clientX, clientY);
      if (pos?.offsetNode === ta && typeof pos.offset === "number") {
        return Math.max(0, Math.min(len, pos.offset));
      }
    } catch {
      /* ignore */
    }
  }

  if (len === 0) return 0;
  // Subpixel / padding can make yContent slightly negative; still map to the first visible row.
  const yProbe = Math.max(0, yContent);

  let lo = 0;
  let hi = len;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const top = textareaCaretLineTopFromContentTop(ta, mid);
    if (top <= yProbe) lo = mid;
    else hi = mid - 1;
  }

  const eps = 2;
  const tRow = textareaCaretLineTopFromContentTop(ta, lo);
  let ls = lo;
  while (ls > 0 && Math.abs(textareaCaretLineTopFromContentTop(ta, ls - 1) - tRow) < eps) {
    ls--;
  }
  let le = lo;
  while (le < len && Math.abs(textareaCaretLineTopFromContentTop(ta, le + 1) - tRow) < eps) {
    le++;
  }

  const lineSlice = ta.value.slice(ls, le + 1);
  const tabSize = Number.parseInt(cs.tabSize || "8", 10) || 8;
  const expanded = expandTabsToSpacesOneLine(lineSlice, tabSize);

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return Math.min(len, ls + lineSlice.length);
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;

  if (xContent <= 0) return ls;

  let col = 0;
  for (; col < expanded.length; col++) {
    const w = ctx.measureText(expanded.slice(0, col + 1)).width;
    if (w > xContent) break;
  }
  col = Math.min(col, lineSlice.length);
  return Math.min(len, ls + col);
}
